import React from "react";
import { Modal, Box, Button, Divider, IconButton, styled } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TextView from "../TextView";
import { tokens } from "../../utils";
import { useSelector, useDispatch } from "react-redux";
import { closeNonAuthenticatedModal } from "../../redux/reducers/nonAuthenticatedModal";
import { useNavigate } from "react-router-dom";
import { selectProfile } from "../../redux/reducers/gallery";

const StyledModalContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  // DL-098: was a hard `width: 519`, which overflows a 390px phone by 129px on
  // what is an ACQUISITION surface (this gate is the only signup prompt an
  // anonymous visitor sees). Cap to the viewport instead; 519 is unchanged
  // anywhere there is room for it.
  width: "min(519px, calc(100vw - 32px))",
  maxHeight: "calc(100vh - 32px)",
  overflowY: "auto",
  boxSizing: "border-box",
  borderRadius: "8px",
  border: "1px solid #E1E1E1",
  background: "#FFFFFF",
  boxShadow:
    "0px 0px 8.9px 0px #00000040, 0px 0px 0px 1px #E1E1E1, 0px 1px 1px 0px #00000024",
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
}));

const HeaderContainer = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const TitleContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
}));

const FooterContainer = styled(Box)(() => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: "16px",
  marginTop: "10px",
}));

const NonAuthenticatedModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    isOpen,
    title,
    headerIcon: HeaderIconComponent,
    action,
    type,
    headerIconProps,
    referralCode,
  } = useSelector((state) => state.nonAuthenticatedModal);
  const profile = useSelector(selectProfile);

  const handleClose = () => {
    dispatch(closeNonAuthenticatedModal());
  };

  const handleSignUpClick = () => {
    handleClose();
    let signupPath = "/signup";
    if (referralCode) {
      signupPath = `/signup/${referralCode}`;
    }
    if(profile?.code) {
      signupPath = `/signup/${profile?.code}`;
    }
    navigate(signupPath);
  };

  const handleLoginClick = () => {
    handleClose();
    navigate("/login");
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.75)", // Darker backdrop
          },
        },
      }}
    >
      <StyledModalContent
        // MUI's <Modal> renders role="presentation" on its root and puts NO role
        // on the content, so this gate reached assistive tech as an unnamed,
        // un-announced <div>. Measured 2026-08-01 on main.f563cd08.js: a
        // role="dialog" query on the live page returned 0 while the gate was
        // open. Naming it here is the whole a11y contract for the control.
        role="dialog"
        aria-modal="true"
        aria-labelledby="non-auth-gate-title"
        aria-describedby="non-auth-gate-body"
        data-testid="non-auth-gate"
      >
        <HeaderContainer>
          <TitleContainer>
            {HeaderIconComponent && (
              <HeaderIconComponent
                width={32}
                height={32}
                {...headerIconProps}
              />
            )}
            <TextView
              id="non-auth-gate-title"
              variant="Header3"
              style={{
                fontFamily: "DM Sans",
                fontWeight: 700,
                fontSize: "20px",
                lineHeight: "20px",
                color: "#0F0F0F",
              }}
            >
              {title}
            </TextView>
          </TitleContainer>
          <IconButton
            onClick={handleClose}
            aria-label="Close"
            // 32px is the ruled minimum click surface; padding:0 on a bare icon
            // left this at the glyph's own ~24px.
            sx={{ padding: 0, width: 32, height: 32 }}
          >
            <CloseIcon />
          </IconButton>
        </HeaderContainer>

        <Divider sx={{ border: "1px solid #E1E1E1" }} />

        <TextView
          id="non-auth-gate-body"
          variant="subtitle"
          style={{
            fontFamily: "DM Sans",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "20px",
            color: "#565556",
          }}
        >
          It looks like you are not logged in! Login or create an iYuga account
          to {action} this {type}.
        </TextView>

        <FooterContainer>
          <Button
            variant="outlined"
            sx={{
              width: 81,
              height: 36,
              borderRadius: "4px",
              border: "1px solid #9B5AF8",
              padding: "6px 12px",
              background: "#FFFFFF",
              textTransform: "none",
              color: "#9B5AF8",
              "&:hover": {
                border: "1px solid #9B5AF8",
                background: "#F3ECFE",
              },
            }}
            onClick={handleSignUpClick}
          >
            Sign Up
          </Button>
          <Button
            variant="contained"
            sx={{
              width: 64,
              height: 36,
              borderRadius: "4px",
              padding: "6px 12px",
              // DL-004 (ISP-4018): white-text fill -> on-fill token (#8F38EF, AA 5.28:1).
              background: tokens.brand.primaryActive,
              textTransform: "none",
              color: "#FFFFFF",
              "&:hover": {
                background: "#7A18E6",
              },
            }}
            onClick={handleLoginClick}
          >
            Login
          </Button>
        </FooterContainer>
      </StyledModalContent>
    </Modal>
  );
};

export default NonAuthenticatedModal;
